import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { CalendarIcon, CheckCircle2, ArrowRight, ArrowLeft, Upload } from "lucide-react";
import { format } from "date-fns";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { api, SERVICES, formatError } from "@/lib/api";

const initial = {
  customer_name: "", phone: "", email: "",
  address: "", city: "",
  preferred_date: "", preferred_time: "",
  service_type: "Deep Cleaning",
  property_size: "", bedrooms: "", bathrooms: "",
  furnished: "Furnished", frequency: "one-time",
  special_requests: "", access_instructions: "",
  preferred_contact: "phone",
  notes: "",
  photos: [],
};

const STEPS_KEYS = ["service", "property", "schedule", "contact"];

export default function Quote() {
  const { t } = useTranslation();
  const STEPS = STEPS_KEYS.map((k) => t(`quote_page.steps.${k}`));
  const [step, setStep] = useState(0);
  const [form, setForm] = useState(initial);
  const [date, setDate] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [uploading, setUploading] = useState(false);

  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const next = () => setStep((s) => Math.min(STEPS.length - 1, s + 1));
  const back = () => setStep((s) => Math.max(0, s - 1));

  const canNext = () => {
    if (step === 0) return !!form.service_type;
    if (step === 1) return !!form.address && !!form.city;
    if (step === 2) return true;
    return true;
  };

  const onUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      const ids = [];
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        fd.append("folder", "leads");
        const r = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        ids.push(r.data.id);
      }
      upd("photos", [...form.photos, ...ids]);
      toast.success(`${files.length} photo(s) uploaded`);
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const submit = async () => {
    if (!form.customer_name || !form.phone) {
      toast.error(t("quote_page.msg_fill_name_phone"));
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        bedrooms: form.bedrooms ? parseInt(form.bedrooms) : null,
        bathrooms: form.bathrooms ? parseInt(form.bathrooms) : null,
        preferred_date: date ? format(date, "yyyy-MM-dd") : "",
      };
      await api.post("/public/leads", payload);
      setDone(true);
    } catch (e) {
      toast.error(formatError(e));
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="max-w-2xl mx-auto px-5 md:px-8 py-24 text-center">
        <div className="w-16 h-16 rounded-full bg-[#5BA83F]/15 flex items-center justify-center mx-auto">
          <CheckCircle2 className="w-8 h-8 text-[#3F7A1F]" />
        </div>
        <h1 className="mt-6 font-heading text-3xl md:text-4xl font-bold text-[#1B3A6B]">{t("quote_page.success_title")}</h1>
        <p className="mt-4 text-slate-600 leading-relaxed">
          {t("quote_page.success_subtitle")}
        </p>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-5 md:px-8 py-12 md:py-16">
      <p className="text-xs uppercase tracking-[0.2em] font-bold text-[#5BA83F]">{t("quote_page.eyebrow")}</p>
      <h1 className="font-heading text-3xl md:text-5xl font-extrabold text-[#1B3A6B] mt-3 tracking-tight">
        {t("quote_page.title")}
      </h1>
      <p className="mt-3 text-slate-600">{t("quote_page.subtitle")}</p>

      {/* Stepper */}
      <div className="mt-10 flex items-center gap-2" data-testid="quote-stepper">
        {STEPS.map((s, i) => (
          <React.Fragment key={s}>
            <div className={`flex items-center gap-2 ${i === step ? "text-[#1B3A6B]" : "text-slate-400"}`}>
              <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-semibold ${i <= step ? "bg-[#1B3A6B] text-white" : "bg-slate-200 text-slate-500"}`}>
                {i + 1}
              </div>
              <span className="text-sm font-medium hidden sm:inline">{s}</span>
            </div>
            {i < STEPS.length - 1 && <div className="flex-1 h-px bg-slate-200" />}
          </React.Fragment>
        ))}
      </div>

      <div className="mt-8 bg-white rounded-2xl p-6 md:p-8 ring-1 ring-slate-200">
        {step === 0 && (
          <div className="space-y-5">
            <h2 className="font-heading text-xl font-semibold text-[#0F172A]">{t("quote_page.step_service_title", "What kind of cleaning do you need?")}</h2>
            <div>
              <Label>{t("quote_page.service_type")}</Label>
              <Select value={form.service_type} onValueChange={(v) => upd("service_type", v)}>
                <SelectTrigger className="mt-1.5" data-testid="quote-service-type"><SelectValue /></SelectTrigger>
                <SelectContent>{SERVICES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>{t("quote_page.how_often")}</Label>
              <RadioGroup value={form.frequency} onValueChange={(v) => upd("frequency", v)} className="mt-2 grid grid-cols-3 gap-3">
                {["one-time", "weekly", "bi-weekly", "monthly"].map((opt) => (
                  <label key={opt} className={`cursor-pointer rounded-lg border px-3 py-2.5 text-sm capitalize text-center ${form.frequency === opt ? "border-[#5BA83F] bg-[#E8F5E0] text-[#3F7A1F] font-semibold" : "border-slate-200 hover:border-slate-300"}`}>
                    <RadioGroupItem value={opt} className="sr-only" />
                    {opt}
                  </label>
                ))}
              </RadioGroup>
            </div>
            <div>
              <Label htmlFor="special">{t("quote_page.special_requests")}</Label>
              <Textarea id="special" value={form.special_requests} onChange={(e) => upd("special_requests", e.target.value)} placeholder={t("quote_page.special_ph")} className="mt-1.5" data-testid="quote-special-requests" />
            </div>
          </div>
        )}

        {step === 1 && (
          <div className="space-y-5">
            <h2 className="font-heading text-xl font-semibold text-[#0F172A]">About the property</h2>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="addr">Full service address</Label>
                <Input id="addr" value={form.address} onChange={(e) => upd("address", e.target.value)} placeholder="Street and number" className="mt-1.5" data-testid="quote-address" />
              </div>
              <div>
                <Label htmlFor="city">City</Label>
                <Input id="city" value={form.city} onChange={(e) => upd("city", e.target.value)} placeholder="Amsterdam" className="mt-1.5" data-testid="quote-city" />
              </div>
              <div>
                <Label htmlFor="size">Property size (m²)</Label>
                <Input id="size" value={form.property_size} onChange={(e) => upd("property_size", e.target.value)} placeholder="e.g. 85" className="mt-1.5" />
              </div>
              <div>
                <Label>Furnished?</Label>
                <Select value={form.furnished} onValueChange={(v) => upd("furnished", v)}>
                  <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Furnished">Furnished</SelectItem>
                    <SelectItem value="Empty">Empty</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="beds">Bedrooms</Label>
                <Input id="beds" type="number" min="0" value={form.bedrooms} onChange={(e) => upd("bedrooms", e.target.value)} className="mt-1.5" />
              </div>
              <div>
                <Label htmlFor="baths">Bathrooms</Label>
                <Input id="baths" type="number" min="0" value={form.bathrooms} onChange={(e) => upd("bathrooms", e.target.value)} className="mt-1.5" />
              </div>
            </div>
            <div>
              <Label htmlFor="access">Access instructions</Label>
              <Textarea id="access" value={form.access_instructions} onChange={(e) => upd("access_instructions", e.target.value)} placeholder="Key location, building code, concierge…" className="mt-1.5" />
            </div>
            <div>
              <Label>Photos (optional)</Label>
              <div className="mt-1.5">
                <label className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-dashed border-slate-300 text-sm cursor-pointer hover:bg-slate-50">
                  <Upload className="w-4 h-4" />
                  {uploading ? "Uploading…" : "Upload photos"}
                  <input type="file" className="hidden" accept="image/*" multiple onChange={onUpload} disabled={uploading} data-testid="quote-photo-upload" />
                </label>
                {form.photos.length > 0 && (
                  <p className="mt-2 text-xs text-slate-500">{form.photos.length} photo(s) attached</p>
                )}
              </div>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-5">
            <h2 className="font-heading text-xl font-semibold text-[#0F172A]">When works for you?</h2>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label>Preferred date</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="mt-1.5 w-full justify-start font-normal" data-testid="quote-date-trigger">
                      <CalendarIcon className="w-4 h-4 mr-2" />
                      {date ? format(date, "PPP") : "Pick a date"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar mode="single" selected={date} onSelect={setDate} disabled={(d) => d < new Date(new Date().setHours(0,0,0,0))} initialFocus />
                  </PopoverContent>
                </Popover>
              </div>
              <div>
                <Label htmlFor="time">Preferred time</Label>
                <Select value={form.preferred_time} onValueChange={(v) => upd("preferred_time", v)}>
                  <SelectTrigger className="mt-1.5" id="time"><SelectValue placeholder="Pick a time" /></SelectTrigger>
                  <SelectContent>
                    {["08:00","09:00","10:00","11:00","13:00","14:00","15:00","16:00"].map((t) => (
                      <SelectItem key={t} value={t}>{t}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label htmlFor="notes">Other notes (optional)</Label>
              <Textarea id="notes" value={form.notes} onChange={(e) => upd("notes", e.target.value)} className="mt-1.5" />
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="space-y-5">
            <h2 className="font-heading text-xl font-semibold text-[#0F172A]">Your contact details</h2>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="name">Full name</Label>
                <Input id="name" value={form.customer_name} onChange={(e) => upd("customer_name", e.target.value)} className="mt-1.5" data-testid="quote-name" />
              </div>
              <div>
                <Label htmlFor="phone">Phone number</Label>
                <Input id="phone" value={form.phone} onChange={(e) => upd("phone", e.target.value)} placeholder="+31 …" className="mt-1.5" data-testid="quote-phone" />
              </div>
              <div className="md:col-span-2">
                <Label htmlFor="email">Email (optional)</Label>
                <Input id="email" type="email" value={form.email} onChange={(e) => upd("email", e.target.value)} className="mt-1.5" data-testid="quote-email" />
              </div>
            </div>
            <div>
              <Label>Preferred contact</Label>
              <RadioGroup value={form.preferred_contact} onValueChange={(v) => upd("preferred_contact", v)} className="mt-2 flex gap-3">
                {["phone", "whatsapp", "email"].map((opt) => (
                  <label key={opt} className={`cursor-pointer rounded-full border px-4 py-1.5 text-sm capitalize ${form.preferred_contact === opt ? "border-[#5BA83F] bg-[#E8F5E0] text-[#3F7A1F] font-semibold" : "border-slate-200 hover:border-slate-300"}`}>
                    <RadioGroupItem value={opt} className="sr-only" /> {opt}
                  </label>
                ))}
              </RadioGroup>
            </div>
          </div>
        )}

        <div className="mt-8 flex items-center justify-between">
          <Button variant="ghost" onClick={back} disabled={step === 0} data-testid="quote-back-btn">
            <ArrowLeft className="w-4 h-4 mr-1" /> {t("quote_page.back")}
          </Button>
          {step < STEPS.length - 1 ? (
            <Button onClick={next} disabled={!canNext()} className="bg-[#1B3A6B] hover:bg-[#152D54] text-white rounded-full px-6" data-testid="quote-next-btn">
              {t("quote_page.next")} <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          ) : (
            <Button onClick={submit} disabled={submitting} className="bg-[#5BA83F] hover:bg-[#4A8A33] text-white rounded-full px-6" data-testid="quote-submit-btn">
              {submitting ? t("quote_page.submitting") : t("quote_page.submit")}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
